import * as THREE from 'three/webgpu';

import { SketchDrawingTool } from '../../draw/DrawingSharedTypes';
import { ImportedModelRaycastResolver } from '../../imported-model/selection/ImportedModelRaycastResolver';
import { ImportedModelTopologySelectionService } from '../../imported-model/selection/ImportedModelTopologySelectionService';
import {
    ContourDragStrategy,
    LineDragStrategy,
    PointDragStrategy,
    type AbstractEntityDragStrategy,
} from '../drag';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import type {EntitySelectionMode} from '../selection/EntitySelectionMode';
import { isSketchObjectEditableInActiveSketch } from '../SketchEditingInteractionGuard';
import {
    isSketchCircleObject,
    isSketchContour,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
    SketchArcObject,
    type SketchContour,
} from '../../mesh';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import {
    isSketchPointBoxSelectable,
    SketchLineObject,
    SketchPointObject,
} from '../../mesh/sketch';
import { AbstractEntitySelectionHandler } from './AbstractEntitySelectionHandler';

/**
 * 草图模式实体交互处理类。
 * 说明：
 * 1. 当前草图模式选择语义与默认模式保持一致；
 * 2. 该类直接继承 AbstractEntitySelectionHandler，避免继承具体处理器；
 * 3. 后续草图模式如需独立演进，可在本类直接收口。
 */
export class SketchEntitySelectionHandler extends AbstractEntitySelectionHandler {
    /** 导入模型射线解析器。 */
    private readonly importedModelRaycastResolver: ImportedModelRaycastResolver;

    /** 导入模型拓扑选择服务。 */
    private readonly importedModelTopologySelectionService: ImportedModelTopologySelectionService;

    /**
     * 构造函数。
     * @param entitySelectionManager 实体选择管理器。
     */
    public constructor(entitySelectionManager: EntitySelectionManager) {
        super(entitySelectionManager);
        this.importedModelRaycastResolver = new ImportedModelRaycastResolver(entitySelectionManager.renderer);
        this.importedModelTopologySelectionService = new ImportedModelTopologySelectionService({
            renderer: entitySelectionManager.renderer,
            importedModelRaycastResolver: this.importedModelRaycastResolver,
        });
    }

    /** 释放草图模式借用的导入拓扑缓存及基类临时显示资源。 */
    public override dispose(): void {
        this.importedModelTopologySelectionService.dispose();
        super.dispose();
    }

    /**
     * 判断命中对象是否为当前活动草图下的可编辑目标。
     *
     * 设计说明：
     * 1. 候选收集阶段已经过滤其它草图对象，这里继续保留拖拽启动前的最终防线；
     * 2. 拖拽会修改草图定义，必须再次确认命中对象属于当前活动草图；
     * 3. 访问链经由 `renderer.sketchGroupManager?.activeSketchGroup?.uuid` 已验证可达。
     *
     * @param targetObject 命中对象。
     * @returns 是否允许升级为拖拽。
     */
    private canEditPickedObject(targetObject: THREE.Object3D): boolean {
        return isSketchObjectEditableInActiveSketch({
            activeSketchGroupId:
                this.entitySelectionManager.renderer.sketchGroupManager?.activeSketchGroup?.uuid ?? null,
            targetObject,
        });
    }

    //------ 活动草图候选过滤 开始------
    /**
     * 过滤草图编辑态候选对象。
     *
     * 设计说明：
     * 1. SolidWorks 编辑草图时，普通点选、悬浮和框选只应作用于当前草图实体；
     * 2. Convert Entities 可通过选择管理器显式放行外部草图候选，但不会放宽拖拽编辑守卫；
     * 3. 过滤收口在候选集阶段，避免后续点击、hover、框选和实体变换命令各自重复判断；
     * 4. 非草图对象保持透传，由转换实体引用、基准参考等具体命令继续按自身选择模式裁剪。
     *
     * @param targets 原始候选对象列表。
     * @returns 仅保留当前活动草图相关对象后的候选对象列表。
     */
    private filterSketchSelectionTargets(targets: THREE.Object3D[]): THREE.Object3D[] {
        // 在候选集阶段完成过滤，后续点选、hover、框选和实体变换命令无需重复关心草图归属。
        return targets.filter((target) => (
            this.entitySelectionManager.isSketchObjectSelectable(target)
        ));
    }
    //------ 活动草图候选过滤 结束------

    /**
     * 判断当前草图选择态是否应接入 imported-model 拓扑拾取。
     *
     * @returns 需要接入 imported-model 拓扑拾取时返回 `true`。
     */
    protected shouldEnableImportedTopologySelection(): boolean {
        const selectionMode = this.entitySelectionManager.getSelectionMode();
        return selectionMode === 'edge'
            || selectionMode === 'face'
            || selectionMode === 'face-edge';
    }

    /**
     * 合并草图候选与 imported-model 候选。
     *
     * @param baseTargets 草图选择候选。
     * @returns 去重后的候选集合。
     */
    protected mergeImportedTopologySelectionTargets(baseTargets: THREE.Object3D[]): THREE.Object3D[] {
        if (!this.shouldEnableImportedTopologySelection()) {
            return baseTargets;
        }

        const targetMap = new Map<string, THREE.Object3D>();
        baseTargets.forEach((target) => {
            targetMap.set(target.uuid, target);
        });
        this.importedModelTopologySelectionService.collectRaycastTargets().forEach((target) => {
            if (!targetMap.has(target.uuid)) {
                targetMap.set(target.uuid, target);
            }
        });
        return Array.from(targetMap.values());
    }

    /**
     * 在草图命令态内解析 imported-model 拓扑命中。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param interactableObjects 当前候选对象。
     * @returns imported-model 拓扑命中；未命中时返回 `null`。
     */
    protected pickImportedTopologyAt(
        clientX: number,
        clientY: number,
        interactableObjects: THREE.Object3D[],
    ): THREE.Intersection<THREE.Object3D> | null {
        if (!this.shouldEnableImportedTopologySelection()) {
            return null;
        }

        return this.importedModelTopologySelectionService.pickAt({
            clientX,
            clientY,
            interactableObjects,
            selectionMode: this.resolveImportedTopologySelectionMode(),
            allowOccludedSelection: this.entitySelectionManager.getAllowOccludedFeatureTopologySelection(),
        });
    }

    /**
     * 从草图候选与 imported-model 候选中选择最终命中。
     *
     * @param sketchPick 草图/特征基础命中。
     * @param importedPick imported-model 拓扑命中。
     * @returns 最终命中。
     */
    protected resolveSketchModePick(
        sketchPick: THREE.Intersection<THREE.Object3D> | null,
        importedPick: THREE.Intersection<THREE.Object3D> | null,
    ): THREE.Intersection<THREE.Object3D> | null {
        if (!sketchPick) {
            return importedPick;
        }
        if (!importedPick) {
            return sketchPick;
        }

        /**
         * 草图编辑态下同距离命中仍保持草图优先，避免模型拓扑抢走当前草图实体。
         */
        return sketchPick.distance <= importedPick.distance ? sketchPick : importedPick;
    }

    /**
     * 解析 imported-model 二阶段拓扑拾取模式。
     *
     * 普通草图沿用全局选择模式；需要同时选择模型边和模型面的具体命令可覆盖此扩展点。
     * @returns 当前 imported-model 拓扑拾取模式。
     */
    protected resolveImportedTopologySelectionMode(): EntitySelectionMode {
        return this.entitySelectionManager.getSelectionMode();
    }

    /**
     * 收集点击选择候选。
     *
     * @returns 点击选择候选。
     */
    protected override collectClickSelectionTargets(): THREE.Object3D[] {
        return this.mergeImportedTopologySelectionTargets(
            this.filterSketchSelectionTargets(super.collectClickSelectionTargets()),
        );
    }

    /**
     * 收集悬停选择候选。
     *
     * @returns 悬停选择候选。
     */
    protected override collectHoverSelectionTargets(): THREE.Object3D[] {
        return this.mergeImportedTopologySelectionTargets(
            this.filterSketchSelectionTargets(super.collectHoverSelectionTargets()),
        );
    }

    /**
     * 解析本次选择需要随事件发布的原始命中。
     *
     * 设计说明：
     * 1. 普通草图选择会把槽口内部边提升为槽口整体，便于整体移动与属性编辑；
     * 2. 转换实体引用这类命令需要知道用户真实点到的是哪条边；
     * 3. 只有语义选中发生提升时才额外传递原始命中，避免普通选择事件携带重复数据。
     *
     * @param rawPicked 未经过语义提升的拾取结果。
     * @param selectedPicked 最终用于选择状态的拾取结果。
     * @returns 需要作为选择来源发布的命中集合。
     */
    private resolveSelectionSourcePicks(
        rawPicked: THREE.Intersection<THREE.Object3D> | null,
        selectedPicked: THREE.Intersection<THREE.Object3D> | null,
    ): THREE.Intersection<THREE.Object3D>[] | undefined {
        if (!rawPicked || !selectedPicked || rawPicked.object === selectedPicked.object) {
            return undefined;
        }
        if (isSketchContour(rawPicked.object)) {
            return undefined;
        }
        return [rawPicked];
    }

    //------ 轮廓选择目标解析 开始------
    /**
     * 解析轮廓点击应写入选择集的对象。
     *
     * 设计说明：
     * 1. 射线归一化必须保留 `SketchContour`，这样拖拽链路才能启动轮廓整体移动；
     * 2. 选择语义不能直接复用拖拽命中对象，圆/椭圆/槽口轮廓应选中源曲线对象；
     * 3. 由多条边组成的闭合轮廓继续选中全部关联边，保持矩形与线弧混合轮廓一致；
     * 4. 未知轮廓来源回退到轮廓本体，避免点击后完全没有选择反馈。
     *
     * @param contour 命中的轮廓对象。
     * @returns 应写入选择集的对象列表。
     */
    private resolveContourSelectionTargets(contour: SketchContour): THREE.Object3D[] {
        const contourSource = contour.source;

        // 圆、椭圆和槽口是单一草图实体，点击其填充轮廓时应选中实体本身，而拖拽仍使用轮廓对象。
        if (
            isSketchCircleObject(contourSource)
            || isSketchEllipseObject(contourSource)
            || isSketchSlotObject(contourSource)
        ) {
            return [contourSource];
        }

        //------ 移动实体混合边轮廓选择 开始------
        // 闭合轮廓需要回到全部关联边，保证线弧混合区域不会只选中直线。
        const contourLinkedEntities = this.resolveContourLinkedSketchEntities(contour);
        if (contourLinkedEntities.length > 0) {
            return contourLinkedEntities;
        }
        //------ 移动实体混合边轮廓选择 结束------

        // 兜底保留轮廓本体，防止新轮廓来源尚未接入时点击没有任何状态更新。
        return [contour];
    }
    //------ 轮廓选择目标解析 结束------

    /**
     * 处理鼠标按下。
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.isSketchDrawingActive()) {
            return;
        }

        //------ 左键框选与 Ctrl+左键多选 开始------
        // 当 Ctrl+左键时，禁用点拖拽与轮廓拖拽，允许点击增加多选继续处理。
        if (payload.ctrlKey) {
            this.entitySelectionManager.setMouseDownStart(new THREE.Vector2(payload.clientX, payload.clientY));
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }
        //------ 左键框选与 Ctrl+左键多选 结束------

        const list = this.collectClickSelectionTargets();
        const picked = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            list,
        );

        if (picked?.object instanceof SketchPointObject) {
            if (
                this.entitySelectionManager.renderer.sketchDrawingManager?.isDrawingActive()
                && (this.entitySelectionManager.renderer.sketchDrawingManager?.getCurrentTool() || null) === SketchDrawingTool.Point
            ) {
                return;
            }

            const pointObject = picked.object;
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(false);

            if (payload.ctrlKey && this.entitySelectionManager.isObjectSelected(pointObject)) {
                this.entitySelectionManager.updateSelectionState(
                    this.resolveNextSelectionState(this.toIntersections([pointObject]), true),
                );
                this.entitySelectionManager.setSuppressNextClick(true);
                payload.nativeEvent.preventDefault();
                return;
            }

            if (this.entitySelectionManager.getCurrentSelectedPoint() !== pointObject) {
                this.entitySelectionManager.updateSelectionState(
                    this.resolveNextSelectionState(this.toIntersections([pointObject]), payload.ctrlKey),
                );
            }

            if (!pointObject.canDrag()) {
                this.entitySelectionManager.setSuppressNextClick(true);
                payload.nativeEvent.preventDefault();
                return;
            }

            if (!this.isPointDragEnabled()) {
                this.entitySelectionManager.setSuppressNextClick(true);
                payload.nativeEvent.preventDefault();
                return;
            }

            if (!this.canEditPickedObject(pointObject)) {
                // 非活动草图的点：保留选中视觉反馈，但不允许进入拖拽编辑
                this.entitySelectionManager.setSuppressNextClick(true);
                payload.nativeEvent.preventDefault();
                return;
            }

            this.startDrag(new PointDragStrategy(this.entitySelectionManager.renderer, pointObject), payload);
            this.entitySelectionManager.setSuppressNextClick(true);
            payload.nativeEvent.preventDefault();
            return;
        }

        if (picked && isSketchContour(picked.object)) {
            const contour = picked.object;
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(false);

            const contourSelectionTargets = this.resolveContourSelectionTargets(contour);
            this.entitySelectionManager.updateSelectionState(
                this.resolveNextSelectionState(this.toIntersections(contourSelectionTargets), payload.ctrlKey),
            );

            if (this.isContourDragEnabled() && this.canEditPickedObject(contour)) {
                this.startDrag(new ContourDragStrategy(this.entitySelectionManager.renderer, contour), payload);
            }
            this.entitySelectionManager.setSuppressNextClick(true);
            payload.nativeEvent.preventDefault();
            return;
        }

        if (picked && isSketchLineObject(picked.object)) {
            const lineObject = picked.object;
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(false);
            this.entitySelectionManager.updateSelectionState(
                this.resolveNextSelectionState([picked], false),
            );

            if (
                this.isLineDragEnabled()
                && lineObject.canDrag()
                && this.canEditPickedObject(lineObject)
            ) {
                this.startDrag(
                    new LineDragStrategy(this.entitySelectionManager.renderer, lineObject),
                    payload,
                );
            }
            this.entitySelectionManager.setSuppressNextClick(true);
            payload.nativeEvent.preventDefault();
            return;
        }

        this.entitySelectionManager.setMouseDownStart(new THREE.Vector2(payload.clientX, payload.clientY));
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();
        this.entitySelectionManager.setSuppressNextClick(false);
    }

    /**
     * 处理鼠标移动。
     * @param payload 原始事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        const activeDragStrategy = this.entitySelectionManager.getActiveDragStrategy();
        if (activeDragStrategy) {
            activeDragStrategy.update(payload);
            return;
        }

        //------ 左键框选与 Ctrl+左键多选 开始------
        // 左键拖动即触发框选；Ctrl 仅影响点击时的多选语义，不再作为框选前置条件。
        const mouseDownStart = this.entitySelectionManager.getMouseDownStart();
        if (mouseDownStart && payload.buttons !== 0) {
            const dx = payload.clientX - mouseDownStart.x;
            const dy = payload.clientY - mouseDownStart.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > this.entitySelectionManager.getBoxDragThreshold() || this.entitySelectionManager.getIsBoxDragging()) {
                this.entitySelectionManager.setIsBoxDragging(true);
                this.entitySelectionManager.setBoxDragEnd(new THREE.Vector2(payload.clientX, payload.clientY));
                this.clearHoverState();
                this.updateSelectionBoxVisual(mouseDownStart, new THREE.Vector2(payload.clientX, payload.clientY));
                return;
            }
        }

        // 按键拖动阶段不执行 hover 拾取，避免与相机操作互相干扰。
        if (payload.buttons !== 0) {
            this.clearHoverState();
            return;
        }

        // 当所有鼠标按钮都释放时，清空 mouseDownStart，防止框选基于过期起点被意外触发
        if (payload.buttons === 0 && this.entitySelectionManager.getMouseDownStart()) {
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
        }
        //------ 左键框选与 Ctrl+左键多选 结束------

        this.queueHoverInput(payload.clientX, payload.clientY);
    }

    /**
     * 处理鼠标抬起。
     * @param payload 原始事件。
     */
    public override onMouseUp(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }

        if (this.entitySelectionManager.getActiveDragStrategy()) {
            //------ 轮廓点击空白清空修复 开始------
            // 无位移拖拽（本质点击）不应吞掉后续 click，
            // 否则会导致点击空白时无法走到 onClick 的清空分支。
            const hasGeometryChanged = this.stopDrag();
            //------ 轮廓点击空白清空修复 结束------
            this.entitySelectionManager.setMouseDownStart(null);
            this.entitySelectionManager.setIsBoxDragging(false);
            this.entitySelectionManager.setBoxDragEnd(null);
            this.resetSelectionBoxVisual();
            this.entitySelectionManager.setSuppressNextClick(hasGeometryChanged);
            return;
        }

        const dragStart = this.entitySelectionManager.getMouseDownStart();
        if (!dragStart) {
            return;
        }

        const isBoxDragging = this.entitySelectionManager.getIsBoxDragging();
        this.entitySelectionManager.setMouseDownStart(null);
        this.entitySelectionManager.setIsBoxDragging(false);
        this.entitySelectionManager.setBoxDragEnd(null);
        this.resetSelectionBoxVisual();

        if (isBoxDragging) {
            // 左键拖动统一执行框选；Ctrl 只影响点击多选，不再改变框选的结算语义。
            this.performBoxSelection(dragStart, new THREE.Vector2(payload.clientX, payload.clientY), false);
            this.entitySelectionManager.setSuppressNextClick(true);
        }
    }

    /**
     * 处理鼠标点击。
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.isSketchDrawingActive()) {
            return;
        }
        if (this.entitySelectionManager.getSuppressNextClick()) {
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        const rawPicked = this.pickEntityAt(
            payload.clientX,
            payload.clientY,
            this.collectClickSelectionTargets(),
        );
        const picked = this.resolveClickSelectionPick(rawPicked);

        if (
            picked?.object instanceof SketchPointObject
            && (this.entitySelectionManager.renderer.sketchDrawingManager?.isDrawingActive() || false)
            && (this.entitySelectionManager.renderer.sketchDrawingManager?.getCurrentTool() || null) === SketchDrawingTool.Point
        ) {
            return;
        }
        if (picked && isSketchContour(picked.object)) {
            const contourSelectionPicks = this.toIntersections(
                this.resolveContourSelectionTargets(picked.object),
            );
            this.entitySelectionManager.updateSelectionState(
                this.resolveNextSelectionState(contourSelectionPicks, payload.ctrlKey),
                undefined,
                this.resolveSelectionSourcePicks(rawPicked, contourSelectionPicks[0] ?? picked),
            );
            this.publishContourLinkedObjectsForPick(picked.object);
            return;
        }

        if (picked) {
            this.entitySelectionManager.updateSelectionState(
                this.resolveNextSelectionState([picked], payload.ctrlKey),
                undefined,
                this.resolveSelectionSourcePicks(rawPicked, picked),
            );
            return;
        }

        if (payload.ctrlKey) {
            return;
        }

        //------ 尺寸线/界线命中（ARC-003）开始------
        // 未命中草图实体时先尝试尺寸线 WebGL 命中：点尺寸线 = 选中尺寸（清实体选择）。
        if (this.entitySelectionManager.renderer.selectSketchDimensionAtClient?.({
            x: payload.clientX,
            y: payload.clientY,
        })) {
            this.entitySelectionManager.updateSelectionState([]);
            return;
        }
        // 点空白：同时清除尺寸选中（与实体选择清空对齐）。
        this.entitySelectionManager.renderer.clearSketchDimensionSelection?.();
        //------ 尺寸线/界线命中 结束------

        this.entitySelectionManager.updateSelectionState([]);
    }

    /**
     * 根据屏幕坐标拾取草图或 imported-model 拓扑实体。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @param interactableObjects 可参与拾取的候选对象列表。
     * @returns 命中实体；未命中返回 `null`。
     */
    protected override pickEntityAt(
        clientX: number,
        clientY: number,
        interactableObjects: THREE.Object3D[],
    ): THREE.Intersection<THREE.Object3D> | null {
        const sketchPick = super.pickEntityAt(clientX, clientY, interactableObjects);
        const importedPick = this.pickImportedTopologyAt(clientX, clientY, interactableObjects);
        return this.resolveSketchModePick(sketchPick, importedPick);
    }

    /**
     * 启动拖拽策略。
     * @param strategy 拖拽策略实例。
     * @param payload 拖拽启动事件载荷。
     */
    private startDrag(strategy: AbstractEntityDragStrategy, payload: RawPointerEventPayload): void {
        this.entitySelectionManager.renderer.interactionStateManager?.setRelationMarkerHiddenDuringDrag(true);
        try {
            strategy.start(payload);
            this.entitySelectionManager.setActiveDragStrategy(strategy);
        } catch (error) {
            this.entitySelectionManager.renderer.interactionStateManager?.setRelationMarkerHiddenDuringDrag(false);
            throw error;
        }
    }

    /**
     * 停止当前拖拽策略。
     */
    private stopDrag(): boolean {
        const activeDragStrategy = this.entitySelectionManager.getActiveDragStrategy();
        if (!activeDragStrategy) {
            return false;
        }
        try {
            const affectedSketchId = activeDragStrategy.resolveAffectedSketchId();
            activeDragStrategy.stop();
            // 最终 pending 帧可能只在 stop 中成功，必须在停止后读取真实几何变化状态。
            const hasGeometryChanged = activeDragStrategy.hasGeometryChanged();
            const shouldCommitDefinitionChanges = activeDragStrategy.shouldCommitDefinitionChanges();
            if (shouldCommitDefinitionChanges && hasGeometryChanged && affectedSketchId) {
                this.entitySelectionManager.renderer.sketchGroupManager?.getGroupById(affectedSketchId)?.commitDefinitionChanges({
                    reason: 'drag-finished',
                });
            }
            this.entitySelectionManager.setActiveDragStrategy(null);
            return shouldCommitDefinitionChanges && hasGeometryChanged;
        } finally {
            this.entitySelectionManager.renderer.interactionStateManager?.setRelationMarkerHiddenDuringDrag(false);
        }
    }

    /**
     * 收集测量叠加层点击候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureClickSelectionTargets(): THREE.Object3D[] {
        return this.collectClickSelectionTargets();
    }

    /**
     * 收集测量叠加层移入候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureHoverSelectionTargets(): THREE.Object3D[] {
        return this.collectHoverSelectionTargets();
    }

    /**
     * 收集框选候选对象。
     *
     * 设计说明：
     * 1. 槽口内部边在候选阶段保持为原始边对象，按自身几何参与统一窗口/交叉判定，
     *    命中后再由 {@link promoteBoxSelectionHitObjects} 提升为整个槽口；
     * 2. 禁止在候选阶段提前把内部边替换为槽口整体：槽口整体包围盒会被“完全包含”判定
     *    拒之框外，导致只框住槽口一部分时既选不中槽口、也选不中边；
     * 3. 草图点只有显式可框选角色才参与，普通控制点继续保持排除。
     *
     * @returns 候选对象列表。
     */
    protected override collectBoxSelectionTargets(): THREE.Object3D[] {
        return this.filterSketchSelectionTargets(super.collectBoxSelectionTargets()).filter((object) => {
            // 草图点按框选角色白名单过滤，避免普通端点、中点、控制点误入选框。
            if (object instanceof SketchPointObject) {
                return isSketchPointBoxSelectable(object);
            }
            // 线状草图实体（含槽口内部边与构造中心线）按自身几何参与框选。
            return object instanceof SketchLineObject
                || object instanceof SketchArcObject
                || isSketchSplineObject(object)
                || isSketchStyleSplineObject(object)
                || isSketchCircleObject(object)
                || isSketchEllipseObject(object)
                || isSketchParabolaObject(object);
        });
    }

    /**
     * 把框选命中的槽口组成边归一为整个槽口。
     *
     * 设计说明：
     * 1. 与点击解析阶段的槽口归属提升（点击内部边即选中槽口整体）保持一致；
     * 2. 提升发生在统一窗口/交叉判定之后：左→右需至少完整框住一条组成边，
     *    右→左与边相交即命中，命中后统一归一为槽口整体，
     *    避免“槽口整体包围盒必须完整落入选择框”导致的部分框选失效；
     * 3. 同一槽口多条组成边同时命中时按 UUID 去重，只写入一个槽口对象；
     * 4. 未归属槽口的线/弧及其他草图实体原样返回，不扩大提升范围。
     *
     * @param objects 统一窗口/交叉判定后命中的对象集合。
     * @returns 归一化后的命中对象集合。
     */
    protected override promoteBoxSelectionHitObjects(objects: THREE.Object3D[]): THREE.Object3D[] {
        const promotedMap = new Map<string, THREE.Object3D>();

        objects.forEach((object) => {
            // 只有槽口的组成边（直线/圆弧）需要提升，其余对象保持原命中语义。
            const slotOwner = (object instanceof SketchLineObject || object instanceof SketchArcObject)
                && isSketchSlotObject(object.closedCollection)
                ? object.closedCollection
                : null;

            if (slotOwner) {
                // 多条边同属一个槽口时只保留一个槽口对象。
                if (!promotedMap.has(slotOwner.uuid)) {
                    promotedMap.set(slotOwner.uuid, slotOwner);
                }
                return;
            }

            if (!promotedMap.has(object.uuid)) {
                promotedMap.set(object.uuid, object);
            }
        });

        return Array.from(promotedMap.values());
    }

    /**
     * 发布草图框选结果，并同步尺寸标注选择集合。
     *
     * 设计说明：
     * 1. 实体仍由统一 `SelectionState` 管理，保持拖拽、属性面板与删除链路不变；
     * 2. 尺寸是独立领域聚合，不伪装成 Three.js 草图实体；
     * 3. 两类选择在同一次框选结算中同步更新，使剪贴板能够读取一致的混合选择快照。
     *
     * @param selectedObjects 本次框选命中的草图实体。
     * @param isCrossing 是否为右到左交叉选择。
     * @param selectionRect 相对渲染容器的框选矩形。
     */
    protected override publishBoxSelectionEvents(
        selectedObjects: THREE.Object3D[],
        isCrossing: boolean,
        selectionRect: {minX: number; minY: number; maxX: number; maxY: number},
    ): void {
        this.entitySelectionManager.renderer.selectSketchDimensionsByScreenRect?.({
            selectionRect,
            isCrossing,
        });
        super.publishBoxSelectionEvents(selectedObjects, isCrossing, selectionRect);
    }

    /**
     * 若点击对象属于某个轮廓语义，派发“轮廓关联对象”的多选事件。
     * 说明：
     * 1. 点击轮廓本体时，直接派发该轮廓的关联对象；
     * 2. 点击轮廓关联草图对象（线/圆/椭圆/槽口等）时，回查关联轮廓并派发其关联对象；
     * 3. 仅在关联对象数量大于 1 时派发多选事件，避免单对象噪声事件。
     * @param pickedObject 点击命中的对象。
     */
    private publishContourLinkedObjectsForPick(pickedObject: THREE.Object3D): void {
        const linkedObjects = this.resolveContourLinkedObjectsForPick(pickedObject);
        if (linkedObjects.length <= 1) {
            return;
        }
        this.entitySelectionManager.publishMultipleEntitiesBoxSelected(linkedObjects);
    }

    /**
     * 解析点击对象对应的轮廓关联对象集合。
     * @param pickedObject 点击命中对象。
     * @returns 去重后的轮廓关联对象集合。
     */
    private resolveContourLinkedObjectsForPick(pickedObject: THREE.Object3D): THREE.Object3D[] {
        const linkedObjectMap = new Map<string, THREE.Object3D>();

        const appendLinkedObjects = (contourObject: SketchContour): void => {
            contourObject.getLinkObjects().forEach((object) => {
                if (!linkedObjectMap.has(object.uuid)) {
                    linkedObjectMap.set(object.uuid, object);
                }
            });
        };

        if (isSketchContour(pickedObject)) {
            appendLinkedObjects(pickedObject);
            return Array.from(linkedObjectMap.values());
        }

        this.entitySelectionManager.renderer.scene.traverse((object) => {
            if (!isSketchContour(object)) {
                return;
            }
            if (object.source === pickedObject) {
                appendLinkedObjects(object);
                return;
            }

            const isLinkedObject = object.getLinkObjects().some((linkedObject) => linkedObject.uuid === pickedObject.uuid);
            if (isLinkedObject) {
                appendLinkedObjects(object);
            }
        });

        return Array.from(linkedObjectMap.values());
    }

}
